import { FaTicketAlt, FaSearch, FaUserFriends, FaCar } from "react-icons/fa";
import styled from "styled-components";
import { useNavigate } from "react-router-dom";

const steps = [
  {
    id: 1,
    title: "Find a ride",
    icon: <FaSearch />,
    description:
      "Enter departure, destination, and date. Choose from verified drivers — including females-only rides for women.",
    cta: true,
  },
  {
    id: 2,
    title: "Book online",
    icon: <FaTicketAlt />,
    description:
      "Reserve your seat online with a small GoSawari commission and instantly connect with the driver via chat.",
  },
  {
    id: 3,
    title: "Travel together",
    icon: <FaUserFriends />,
    description:
      "Meet your driver, bring exact cash, and enjoy a smooth shared journey. Plans changed? Cancel easily.",
  },
  {
    id: 4,
    title: "Enjoy the ride",
    icon: <FaCar />,
    description:
      "Sit back, relax, and rate your experience once the ride is complete.",
  },
];

const Timeline = () => {
  const navigate = useNavigate();

  return (
    <Section>
      <Header>
        <span>Simple. Safe. Affordable.</span>
        <h2>How GoSawari Works for Passengers</h2>
      </Header>

      <Grid>
        {steps.map((step) => (
          <Card key={step.id}>
            <StepNumber>{step.id}</StepNumber>

            <IconWrapper>{step.icon}</IconWrapper>

            <h3>{step.title}</h3>
            <p>{step.description}</p>

            {step.cta && (
              <CTA onClick={() => navigate("search-rides")}>Find a Ride →</CTA>
            )}
          </Card>
        ))}
      </Grid>
    </Section>
  );
};

export default Timeline;

const Section = styled.section`
  padding: 5rem 2rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  background: white;

  @media (max-width: 768px) {
    padding: 3rem 1.5rem;
  }
`;

const Header = styled.div`
  text-align: center;
  max-width: 700px;
  margin-bottom: 3rem;

  @media (max-width: 768px) {
    margin-bottom: 2rem;
  }

  span {
    color: #00796b;
    font-weight: 600;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    font-size: 0.85rem;

    @media (max-width: 768px) {
      font-size: 0.75rem;
    }
  }

  h2 {
    margin-top: 0.75rem;
    font-size: 2.4rem;
    font-weight: 700;
    line-height: 1.2;
    color: #054752;
    letter-spacing: -0.5px;

    @media (max-width: 1024px) {
      font-size: 2rem;
    }

    @media (max-width: 768px) {
      font-size: 1.8rem;
    }

    @media (max-width: 480px) {
      font-size: 1.6rem;
      line-height: 1.3;
    }
  }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 2rem;
  max-width: 1200px;
  width: 100%;

  @media (max-width: 1024px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const Card = styled.div`
  position: relative;
  padding: 2.5rem 2rem;
  border-radius: 20px;
  background: white;
  border: 1px solid #e0e0e0;
  transition: all 0.3s ease;
  display: flex;
  flex-direction: column;
  height: 100%;
  box-shadow: 0 4px 10px rgba(0,0,0,0.03);

  &:hover {
    transform: translateY(-10px);
    box-shadow: 0 20px 40px rgba(5, 71, 82, 0.1);
    border-color: #00897b;
  }

  h3 {
    margin: 1.2rem 0 0.75rem;
    font-size: 1.2rem;
    font-weight: 600;
    color: #455a64;
  }

  p {
    font-size: 0.95rem;
    line-height: 1.6;
    color: #78909c;
    flex-grow: 1;
  }
`;

const StepNumber = styled.div`
  position: absolute;
  top: 20px;
  right: 20px;
  font-size: 3.5rem;
  font-weight: 800;
  background: linear-gradient(135deg, #e0f2f1, #b2dfdb);
  -webkit-background-clip: text;
  color: #b2dfdb; /* Fallback */
  opacity: 0.6;
`;

const IconWrapper = styled.div`
  width: 52px;
  height: 52px;
  border-radius: 50%;
  background: linear-gradient(135deg, #00796b, #004d40);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.4rem;
  color: white;
`;

const CTA = styled.button`
  margin-top: 1.5rem;
  padding: 0.8rem 1.4rem;
  border-radius: 999px;
  border: none;
  background: linear-gradient(135deg, #00796b, #004d40);
  color: white;
  font-weight: 600;
  cursor: pointer;
  align-self: flex-start;
  transition: all 0.25s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 10px 20px rgba(0, 77, 64, 0.3);
  }
`;
